import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import AutomatedJobsService, {
  CommonGetReqDTO,
  UpdateInsightsDTO,
} from "src/services/automatedJobsService";
import CommonService, {
  ExtractTextHighlightPayload,
  TranslationPayload,
} from "src/services/commonService";
import {
  INADEQUATE_INFORMATION,
  NO_OBJECT_INVOLVED,
  OTHER,
  HIERARCHY_SEPARATOR,
} from "src/constants";
import ModelLifeCycleService, {
  AssignReviewJobsPayload,
  UpdateAssignmentPayload,
} from "src/services/modelLifeCycleService";
import { LOADING_STATE } from "src/types/constants";
import { generateTimestamp } from "src/utils/dateUtil";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import BatchJobService from "src/services/batchJobService";
import { RootState } from "src/store/store";
import { isAssignedBasedReview } from "src/components/shared/groundTruth/reviewTable/helpers";

export enum ReviewStatus {
  REVIEWED = "Reviewed",
  UNREVIEWED = "Unreviewed",
}

export enum ReviewResult {
  LLM_CORRECT = "LLM is Correct",
  HUMAN_CORRECT = "Human is correct",
  INADEQUATE = "Inadequate Information",
  NEITHER = "Neither is correct",
  BOTH_CORRECT = "Both are correct",
  NOT_REVIEWED_YET = "Not reviewed yet",
}

export type ReviewFieldEntry = {
  fieldName: string;
  answer: string[];
  additionalInputs?: Record<string, string>;
};

export type ReviewEntry = {
  reviewer: string;
  reviewStartedAt: string;
  reviewSubmittedAt: string;
  fields: ReviewFieldEntry[];
};

export type GroundTruthField = {
  fieldName: string;
  userAnswer: string;
  llmClarification: string;
  llmAnswer: string;
  confirmedAnswer: string | string[];
  reviewResult: ReviewResult;
  knowledge?: string;
};

export type GroundTruthData = {
  modelId: string;
  sourceId: string;
  sourceText: string;
  fields: GroundTruthField[];
  timestamp: string;
  status: ReviewStatus;
  reviewer: string;
  reviewContributors?: string[];
  pendingReviewers?: string[];
  reviewHistory?: ReviewEntry[];
  metadata?: Record<string, any>;
  adminReviewers?: string[];
};

export type GroundTruthDataListView = {
  sourceId: string;
  status: ReviewStatus;
  reviewer: string;
  reviewContributors?: string[];
  pendingReviewers?: string[];
  adminReviewers?: string[];
};

export type BatchExtractedField = {
  fieldName: string;
  result: string;
  llmClarification: string;
};

export type BatchExtractedRecord = {
  batchJobId: string;
  sourceId: string;
  sourceText: string;
  modelId: string;
  extractedResult: BatchExtractedField[];
  status: string;
  timestamp: string;
};

const MULTI_OPTION_DELIMITER = "|";

export type GroundTruthState = {
  groundTruthListIndex: number;
  groundTruthDataListView: GroundTruthDataListView[];
  groundTruthDataTotalCount: number;
  groundTruthDataTotalReviewedCount: number;
  assignedReviewTasks: Record<string, number>;
  pendingReviewTasks: Record<string, number>;
  assignedValidationTasks: Record<string, number>;
  pendingValidationTasks: Record<string, number>;
  selectedGroundTruthData: GroundTruthData;
  itemsPerPage: number;
  currentPage: number;
  isBlind: boolean;
  moveToNextWhenSave: boolean;
  showUserAnswerColumn: boolean;
  showLLMAnswerColumn: boolean;
  groundTruthDataFieldIndex: number;
  translation: string;
  showTranslation: boolean;
  showHighlight: boolean;
  highlightField: string;
  pageLoadTimestamp: string;
  additionalInputsByField: Record<number, Record<string, string>>;
  assignmentCSV?: File;
  assignmentError: string;
  aliasFilter: string[];
  filteredGroundTruthDataListView: GroundTruthDataListView[];
  getListViewLoading: LOADING_STATE;
  getGroundTruthDataLoading: LOADING_STATE;
  getGroundTruthDataStatistics: LOADING_STATE;
  updateGroundTruthDataLoading: LOADING_STATE;
  assignReviewJobLoading: LOADING_STATE;
  updateAssignmentLoading: LOADING_STATE;
  translationLoading: LOADING_STATE;
  textHighlightLoading: LOADING_STATE;
  shouldShowError: boolean;
};

export const initialGroundTruthData: GroundTruthData = {
  sourceId: "",
  modelId: "",
  sourceText: "",
  fields: [],
  timestamp: "",
  status: ReviewStatus.UNREVIEWED,
  reviewer: "",
  reviewContributors: [],
  reviewHistory: [],
};

const initialState: GroundTruthState = {
  groundTruthListIndex: -1,
  groundTruthDataListView: [],
  groundTruthDataTotalCount: 0,
  groundTruthDataTotalReviewedCount: 0,
  assignedReviewTasks: {},
  pendingReviewTasks: {},
  assignedValidationTasks: {},
  pendingValidationTasks: {},
  selectedGroundTruthData: initialGroundTruthData,
  itemsPerPage: 50,
  currentPage: 1,
  isBlind: true,
  moveToNextWhenSave: true,
  showUserAnswerColumn: true,
  showLLMAnswerColumn: true,
  groundTruthDataFieldIndex: -1,
  translation: "",
  highlightField: "",
  showTranslation: false,
  showHighlight: true,
  assignmentError: "",
  aliasFilter: [],
  filteredGroundTruthDataListView: [],
  getListViewLoading: "idle",
  getGroundTruthDataLoading: "idle",
  updateGroundTruthDataLoading: "idle",
  assignReviewJobLoading: "idle",
  updateAssignmentLoading: "idle",
  getGroundTruthDataStatistics: "idle",
  translationLoading: "idle",
  textHighlightLoading: "idle",
  pageLoadTimestamp: "",
  additionalInputsByField: {},
  shouldShowError: false,
};

export const GROUND_TRUTH_UPLOAD_URL = "/model_life_cycle/upload";

export const getGroundTruthDataListView = createAsyncThunk(
  "modelLifeCycle/groundTruth/getGroundTruthDataListView",
  async (modelId: string) => {
    const { data } = await ModelLifeCycleService.getGroundTruthDataListView(modelId);
    return data;
  },
);

export const getBatchRecordList = createAsyncThunk(
  "modelLifeCycle/onDemandBatchJob/getBatchRecordList",
  async (batchJobId: string) => {
    const { data } = await BatchJobService.getBatchJobRecordList(batchJobId);
    return data;
  },
);

export const getGroundTruthData = createAsyncThunk(
  "modelLifeCycle/groundTruth/getGroundTruthData",
  async (payload: { modelId: string; sourceId: string }) => {
    const { data } = await ModelLifeCycleService.getGroundTruthData(
      payload.modelId,
      payload.sourceId,
    );
    return data;
  },
);

export const getBatchRecord = createAsyncThunk(
  "modelLifeCycle/onDemandBatchJob/getBatchRecord",
  async (payload: { batchJobId: string; sourceId: string }, { dispatch, getState }) => {
    const { data } = await BatchJobService.getBatchJobRecord({
      batch_job_id: payload.batchJobId,
      source_id: payload.sourceId,
    });
    if (data.status === ReviewStatus.REVIEWED) {
      const selectedModelId = (getState() as RootState).modelSlice.selectedModelId;
      dispatch(getGroundTruthData({ modelId: selectedModelId, sourceId: payload.sourceId }));
    }
    return data;
  },
);

export const getRandomInsight = createAsyncThunk(
  "modelLifeCycle/groundTruth/getRandomInsight",
  async (payload: CommonGetReqDTO) => {
    return await AutomatedJobsService.getRandomInsight(payload);
  },
);

export const getInsightBySourceId = createAsyncThunk(
  "modelLifeCycle/groundTruth/getInsightBySourceId",
  async (payload: { definition_id: string; source_id: string }) => {
    return await AutomatedJobsService.getInsightBySourceId(payload);
  },
);

export const updateInsight = createAsyncThunk<GroundTruthData, UpdateInsightsDTO>(
  "automatedJobs/groundTruth/updateInsight",
  async (payload: UpdateInsightsDTO) => {
    return await AutomatedJobsService.updateInsight(payload);
  },
);

export const assignReviewJobs = createAsyncThunk(
  "modelLifeCycle/groundTruth/assignReviewJobs",
  async (payload: AssignReviewJobsPayload) => {
    const { data } = await ModelLifeCycleService.assignReviewJobs(payload);
    return data;
  },
);

export const downloadGroundTruthsInCSV = createAsyncThunk(
  "modelLifeCycle/getetGroundTruthsCSV",
  async (payload: { modelId: string }) => {
    const { data } = await ModelLifeCycleService.downloadGroundTruthsInCSV(payload);
    return data;
  },
);

export const updateAssignment = createAsyncThunk(
  "modelLifeCycle/groundTruth/updateAssignment",
  async (payload: UpdateAssignmentPayload) => {
    const { data } = await ModelLifeCycleService.updateAssignment(payload);
    return data;
  },
);

const addReviewHistory = (payload: {
  groundTruthData: GroundTruthData;
  reviewer: string;
  pageLoadTimestamp: string;
  additionalInputsByField: Record<number, Record<string, string>>;
}): GroundTruthData => {
  const newReviewEntry: ReviewEntry = {
    reviewer: payload.reviewer,
    reviewStartedAt: payload.pageLoadTimestamp,
    reviewSubmittedAt: generateTimestamp(),
    fields: payload.groundTruthData.fields.map((field, index) =>
      toReviewFieldEntry(field, payload.additionalInputsByField[index]),
    ),
  };

  const currentReviewHistory = payload.groundTruthData.reviewHistory || [];
  const currentReviewContributors = payload.groundTruthData.reviewContributors || [];
  const currentPendingReviewers = payload.groundTruthData.pendingReviewers || [];

  return {
    ...payload.groundTruthData,
    reviewHistory: [...currentReviewHistory, newReviewEntry],
    reviewContributors: currentReviewContributors.includes(payload.reviewer)
      ? currentReviewContributors
      : [...currentReviewContributors, payload.reviewer],
    pendingReviewers: currentPendingReviewers.filter((reviewer) => reviewer !== payload.reviewer),
  };
};

export const convertGroundTruthToBatchRecord = (payload: {
  groundTruthData: GroundTruthData;
  reviewer: string;
  batchJobId: string;
}) => {
  return {
    batchJobId: payload.batchJobId,
    sourceId: payload.groundTruthData.sourceId,
    sourceText: payload.groundTruthData.sourceText,
    modelId: payload.groundTruthData.modelId,
    extractedResult: payload.groundTruthData.fields.map((field) => ({
      fieldName: field.fieldName,
      result: field.confirmedAnswer as string,
      llmClarification: field.llmClarification,
    })),
    status: ReviewStatus.REVIEWED,
    timestamp: payload.groundTruthData.timestamp,
  };
};

export const updateGroundTruthData = createAsyncThunk(
  "modelLifeCycle/groundTruth/updateGroundTruthData",
  async (
    payload: {
      groundTruthData: GroundTruthData;
      reviewer: string;
      pageLoadTimestamp: string;
      batchJobId?: string;
    },
    { getState },
  ) => {
    const { additionalInputsByField } = (getState() as RootState).modelLifeCycleGroundTruthSlice;
    if (payload.batchJobId) {
      await BatchJobService.updateBatchJobRecord(
        convertGroundTruthToBatchRecord({
          groundTruthData: payload.groundTruthData,
          reviewer: payload.reviewer,
          batchJobId: payload.batchJobId,
        }),
      );
    }
    payload.groundTruthData = addReviewHistory({ ...payload, additionalInputsByField });
    const { data } = await ModelLifeCycleService.updateGroundTruthData({
      ...payload.groundTruthData,
      fields: payload.groundTruthData.fields.map((groundTruthField: GroundTruthField) =>
        toGroundTruthFields(groundTruthField),
      ),
      reviewer: payload.reviewer,
    });
    return data;
  },
);

export const addReviewEntry = createAsyncThunk(
  "modelLifeCycle/groundTruth/addReviewEntry",
  async (
    payload: {
      groundTruthData: GroundTruthData;
      pageLoadTimestamp: string;
      reviewer: string;
    },
    { getState },
  ) => {
    const { additionalInputsByField } = (getState() as RootState).modelLifeCycleGroundTruthSlice;
    const { data } = await ModelLifeCycleService.addReviewEntry(
      payload.groundTruthData.modelId,
      payload.groundTruthData.sourceId,
      {
        fields: payload.groundTruthData.fields.map((groundTruthField: GroundTruthField, index) =>
          toReviewFieldEntry(groundTruthField, additionalInputsByField[index]),
        ),
        reviewer: payload.reviewer, // this will be handel by backend
        reviewStartedAt: payload.pageLoadTimestamp,
        reviewSubmittedAt: generateTimestamp(),
      },
    );
    return data;
  },
);

export const translate = createAsyncThunk(
  "model/translation",
  async (payload: TranslationPayload) => {
    const { data } = await CommonService.translate(payload);
    return data;
  },
);

export const extractTextHighlightByFieldDefinition = createAsyncThunk(
  "model/textHighlight",
  async (payload: ExtractTextHighlightPayload) => {
    const { data } = await CommonService.extractTextHighlightByFieldDefinition(payload);
    return data;
  },
);

export const toGroundTruthFields = (item: GroundTruthField) => {
  return {
    ...item,
    confirmedAnswer:
      typeof item.confirmedAnswer === "string"
        ? item.confirmedAnswer
        : item.confirmedAnswer.join(MULTI_OPTION_DELIMITER),
  };
};

export const toReviewFieldEntry = (
  groundTruthField: GroundTruthField,
  additionalInputs?: Record<string, string>,
) => {
  return {
    fieldName: groundTruthField.fieldName,
    answer:
      typeof groundTruthField.confirmedAnswer === "string"
        ? [groundTruthField.confirmedAnswer]
        : groundTruthField.confirmedAnswer,
    ...(additionalInputs && Object.keys(additionalInputs).length > 0 && { additionalInputs }),
  };
};

// Helper function to filter out "llm" and get unique lower case aliases
const processAlias = (aliases?: string[]) => {
  return (
    aliases
      ?.map((alias: string) => alias.toLowerCase())
      .filter((alias) => alias !== "llm")
      ?.filter((alias) => alias !== "llm") || []
  );
};

// Convert BatchExtractedRecord to GroundTruthDataListView
const convertBatchRecordsToListView = (
  records: BatchExtractedRecord[],
): GroundTruthDataListView[] => {
  return records.map((record) => ({
    sourceId: record.sourceId,
    status:
      record.status === ReviewStatus.REVIEWED ? ReviewStatus.REVIEWED : ReviewStatus.UNREVIEWED,
    reviewer: "",
  }));
};

// Reusable function to handle list view data processing
const processListViewData = (state: GroundTruthState, payload: GroundTruthDataListView[]) => {
  state.getListViewLoading = "fulfilled";
  state.groundTruthDataListView = payload;
  state.groundTruthDataTotalCount = payload.length;

  // Initialize counters
  state.assignedReviewTasks = {};
  state.pendingReviewTasks = {};
  state.assignedValidationTasks = {};
  state.pendingValidationTasks = {};
  state.groundTruthDataTotalReviewedCount = 0;

  // Collect all unique non-LLM aliases
  const allAliases: string[] = [];
  payload.forEach((item: GroundTruthDataListView) => {
    allAliases.push(...processAlias(item.reviewContributors));
    allAliases.push(...processAlias(item.pendingReviewers));
    allAliases.push(...processAlias(item.adminReviewers));
  });
  const allUniqueAliases = new Set<string>(allAliases);

  // Initialize counters for all aliases
  allUniqueAliases.forEach((alias) => {
    state.assignedReviewTasks[alias] = 0;
    state.pendingReviewTasks[alias] = 0;
    state.assignedValidationTasks[alias] = 0;
    state.pendingValidationTasks[alias] = 0;
  });

  // Count tasks for each item
  payload.forEach((item: GroundTruthDataListView) => {
    // Count reviewed items
    const nonLLMContributors = processAlias(item.reviewContributors);
    if (nonLLMContributors.length > 0 || item.status === ReviewStatus.REVIEWED) {
      state.groundTruthDataTotalReviewedCount++;
    }

    const reviewers = processAlias(item.reviewContributors);
    const pendingReviewers = processAlias(item.pendingReviewers);
    const adminReviewers = processAlias(item.adminReviewers);

    // Process each non-LLM alias in the item
    new Set([...reviewers, ...pendingReviewers, ...adminReviewers]).forEach((alias) => {
      // Rule 1: Assigned review tasks (if reviewer or pending, but not admin)
      if (
        (reviewers.includes(alias) || pendingReviewers.includes(alias)) &&
        !adminReviewers.includes(alias)
      ) {
        state.assignedReviewTasks[alias]++;
      }

      // Rule 2: Pending review tasks (if pending but not admin)
      if (pendingReviewers.includes(alias) && !adminReviewers.includes(alias)) {
        state.pendingReviewTasks[alias]++;
      }

      // Rule 3: Pending validation tasks (if pending and admin)
      if (pendingReviewers.includes(alias) && adminReviewers.includes(alias)) {
        state.pendingValidationTasks[alias]++;
      }

      // Rule 4: Assigned validation tasks (if admin)
      if (adminReviewers.includes(alias)) {
        state.assignedValidationTasks[alias]++;
      }
    });
  });
};

// Helper function for filtering special values in confirmed answers
const filterSpecialValues = (answers: string[] | string): string[] | string => {
  // Check if a value contains a special value (either directly or in a hierarchical format)
  const containsSpecialValue = (answer: string): boolean => {
    // Direct match
    if (
      compareStringsIgnoreCase(answer, INADEQUATE_INFORMATION) ||
      compareStringsIgnoreCase(answer, NO_OBJECT_INVOLVED) ||
      compareStringsIgnoreCase(answer, OTHER)
    ) {
      return true;
    }

    // Hierarchical match
    if (answer.includes(HIERARCHY_SEPARATOR)) {
      const part = answer.split(HIERARCHY_SEPARATOR)[1]?.trim();
      return part
        ? compareStringsIgnoreCase(part, INADEQUATE_INFORMATION) ||
            compareStringsIgnoreCase(part, NO_OBJECT_INVOLVED) ||
            compareStringsIgnoreCase(part, OTHER)
        : false;
    }

    return false;
  };

  // Handle string value
  if (typeof answers === "string") {
    return answers; // Keep as is if it's a string
  }

  // Handle array value
  if (answers.length === 0) return answers;

  // Find direct special values first (highest priority)
  const directSpecialValues = answers.filter(
    (answer) => !answer.includes(HIERARCHY_SEPARATOR) && containsSpecialValue(answer),
  );
  if (directSpecialValues.length > 0) {
    return [directSpecialValues[0]]; // Return only the first direct special value
  }

  // Process hierarchical values by parent group
  const parentGroups: Record<string, string[]> = {};
  answers.forEach((answer) => {
    const parent = answer.includes(HIERARCHY_SEPARATOR)
      ? answer.split(HIERARCHY_SEPARATOR)[0].trim()
      : answer;
    if (!parentGroups[parent]) parentGroups[parent] = [];
    parentGroups[parent].push(answer);
  });

  // Filter by parent group
  const result: string[] = [];
  Object.values(parentGroups).forEach((group) => {
    const hasSpecial = group.some(containsSpecialValue);
    result.push(...(hasSpecial ? group.filter(containsSpecialValue) : group));
  });

  return result;
};

export const setLLMAnswerForUnreviewedGroundTruthData = (groundTruthData: GroundTruthData) => {
  if (groundTruthData.status === ReviewStatus.UNREVIEWED) {
    groundTruthData.fields.forEach((groundTruthField) => {
      if (compareStringsIgnoreCase(groundTruthField.llmAnswer, groundTruthField.userAnswer)) {
        groundTruthField.confirmedAnswer = groundTruthField.llmAnswer;
        groundTruthField.reviewResult = ReviewResult.LLM_CORRECT;
      } else {
        groundTruthField.confirmedAnswer = "";
        groundTruthField.reviewResult = ReviewResult.NOT_REVIEWED_YET;
      }
    });
  }
  return groundTruthData;
};

/** Data Extraction Page Slice */
const { reducer, actions } = createSlice({
  name: "modelLifeCycle/groundTruthReviewPageSlice",
  initialState,
  reducers: {
    setShouldShowError: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldShowError = payload;
    },
    setCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
    setIsBlind: (state, { payload }) => {
      state.isBlind = payload;
    },
    setMoveToNextWhenSave: (state, { payload }) => {
      state.moveToNextWhenSave = payload;
    },
    updateColumnVisibilityFromModelMetadata: (state, { payload }) => {
      // Check if columns should be shown based on metadata configuration and field content
      const hasUserAnswers = state.selectedGroundTruthData.fields.some(
        (field) => field.userAnswer.trim() !== "",
      );
      const hasLLMAnswers = state.selectedGroundTruthData.fields.some(
        (field) => field.llmAnswer.trim() !== "",
      );

      // Get metadata configuration from the model
      const modelMetadata = payload.modelMetadata;
      const showUserAnswerConfig = modelMetadata?.show_user_answer;
      const showLLMAnswerConfig = modelMetadata?.show_llm_answer;

      // Show user answer column if metadata allows it (not explicitly "false") AND there are user answers
      state.showUserAnswerColumn =
        hasUserAnswers &&
        (showUserAnswerConfig === undefined ||
          String(showUserAnswerConfig).toLowerCase() !== "false");

      // Show LLM answer column if metadata allows it (not explicitly "false") AND there are LLM answers
      state.showLLMAnswerColumn =
        hasLLMAnswers &&
        (showLLMAnswerConfig === undefined ||
          String(showLLMAnswerConfig).toLowerCase() !== "false");
    },
    setItemPerPage: (state, { payload }) => {
      state.itemsPerPage = payload;
    },
    setListViewIndex: (state, { payload }) => {
      state.groundTruthListIndex = state.groundTruthDataListView.findIndex(
        (groundTruthDataView) => groundTruthDataView.sourceId === payload,
      );
    },
    setShowTranslation: (state, { payload }) => {
      state.showTranslation = payload;
    },
    setShowHighlight: (state, { payload }) => {
      state.showHighlight = payload;
    },
    setFieldKnowledge: (state, { payload }) => {
      state.selectedGroundTruthData.fields[payload.fieldIndex].knowledge = payload.knowledge;
    },
    setFieldAdditionalInput: (state, { payload }) => {
      if (!state.additionalInputsByField[payload.fieldIndex]) {
        state.additionalInputsByField[payload.fieldIndex] = {};
      }
      state.additionalInputsByField[payload.fieldIndex][payload.key] = payload.value;
    },
    setHighlightField: (state, { payload }) => {
      state.highlightField = payload;
    },
    setAssignmentCSV: (state, { payload }) => {
      state.assignmentCSV = payload;
    },
    setAliasFilter: (state, { payload }: PayloadAction<string[]>) => {
      state.aliasFilter = payload.map((alias) => alias.toLowerCase().trim());
      state.currentPage = 1; // Reset to first page when filtering
    },
    applyAliasFilter: (state) => {
      if (!state.aliasFilter || state.aliasFilter.length === 0) {
        state.filteredGroundTruthDataListView = state.groundTruthDataListView;
      } else {
        state.filteredGroundTruthDataListView = state.groundTruthDataListView.filter((item) => {
          // Check if any of the filtered aliases is involved in this item
          const reviewContributors =
            item.reviewContributors?.map((alias) => alias.toLowerCase()) || [];
          const pendingReviewers = item.pendingReviewers?.map((alias) => alias.toLowerCase()) || [];
          const adminReviewers = item.adminReviewers?.map((alias) => alias.toLowerCase()) || [];

          return state.aliasFilter.some(
            (filterAlias) =>
              reviewContributors.includes(filterAlias) ||
              pendingReviewers.includes(filterAlias) ||
              adminReviewers.includes(filterAlias),
          );
        });
      }
    },
    setFieldConfirmedAnswer: (state, { payload }) => {
      payload.confirmedAnswer = filterSpecialValues(payload.confirmedAnswer);

      state.selectedGroundTruthData.fields[payload.fieldIndex].confirmedAnswer =
        payload.confirmedAnswer;
      if (
        payload.reviewResult === ReviewResult.BOTH_CORRECT ||
        payload.reviewResult === ReviewResult.INADEQUATE
      ) {
        state.selectedGroundTruthData.fields[payload.fieldIndex].reviewResult =
          payload.reviewResult;
      } else {
        if (
          payload.confirmedAnswer ===
          state.selectedGroundTruthData.fields[payload.fieldIndex].llmAnswer
        ) {
          state.selectedGroundTruthData.fields[payload.fieldIndex].reviewResult =
            ReviewResult.LLM_CORRECT;
        } else if (
          payload.confirmedAnswer ===
          state.selectedGroundTruthData.fields[payload.fieldIndex].userAnswer
        ) {
          state.selectedGroundTruthData.fields[payload.fieldIndex].reviewResult =
            ReviewResult.HUMAN_CORRECT;
        } else {
          state.selectedGroundTruthData.fields[payload.fieldIndex].reviewResult =
            ReviewResult.NEITHER;
        }
      }
      if (
        typeof state.selectedGroundTruthData.fields[payload.fieldIndex].confirmedAnswer ===
          "string" &&
        state.selectedGroundTruthData.fields[payload.fieldIndex].confirmedAnswer.includes(
          MULTI_OPTION_DELIMITER,
        )
      ) {
        state.selectedGroundTruthData.fields[payload.fieldIndex].confirmedAnswer = (
          state.selectedGroundTruthData.fields[payload.fieldIndex].confirmedAnswer as string
        ).split(MULTI_OPTION_DELIMITER);
      }
    },
    updateConfirmedAnswersFromHistory: (state, { payload }) => {
      const alias = payload;

      // Only process if status is UNREVIEWED or has reviewHistory
      if (
        state.selectedGroundTruthData.status === ReviewStatus.REVIEWED ||
        !state.selectedGroundTruthData.reviewHistory
      ) {
        return;
      }
      // Get review entries for the current reviewer
      const currentReviewerEntries = state.selectedGroundTruthData.reviewHistory?.filter(
        (entry) => entry.reviewer === alias,
      );

      // If no previous entries from this reviewer, leave confirmedAnswer as is
      if (!currentReviewerEntries || currentReviewerEntries.length === 0) {
        return;
      }

      // Find the latest review entry by timestamp
      const latestEntry = currentReviewerEntries.reduce((latest, current) => {
        return new Date(current.reviewSubmittedAt) > new Date(latest.reviewSubmittedAt)
          ? current
          : latest;
      }, currentReviewerEntries[0]);

      // Update the confirmed answers based on the latest entry
      state.selectedGroundTruthData.fields = state.selectedGroundTruthData.fields.map(
        (field: GroundTruthField) => ({
          ...field,
          confirmedAnswer:
            latestEntry.fields.find(
              (fieldEntry: ReviewFieldEntry) => fieldEntry.fieldName === field.fieldName,
            )?.answer || field.confirmedAnswer,
        }),
      );
    },
    sortAndFilterGroundTruthDataListView: (state, { payload }) => {
      // Check for assignment-based review using both requiredReviewerCount (legacy, to be deprecated)
      // and metadata.data_label_model (new preferred method)
      const isAssignmentBasedReview = isAssignedBasedReview(payload.model);
      if (!payload.isAdmin && isAssignmentBasedReview) {
        state.groundTruthDataListView = state.groundTruthDataListView.filter(
          (item) =>
            item.pendingReviewers?.includes(payload.alias) ||
            item.reviewContributors?.includes(payload.alias),
        );
      }

      // Sort the list
      state.groundTruthDataListView.sort((a, b) => {
        const getItemPriority = (item: GroundTruthDataListView): number => {
          if (isAssignmentBasedReview) {
            // Check if it's a validation task (admin review after initial review)
            if (
              item.adminReviewers?.includes(payload.alias) &&
              item.pendingReviewers?.includes(payload.alias) &&
              (item.reviewContributors?.length ?? 0) >= 1
            ) {
              return 1; // Highest priority - validation tasks
            }
            // Check if it's a pending review task
            if (
              item.pendingReviewers?.includes(payload.alias) &&
              !item.reviewContributors?.includes(payload.alias)
            ) {
              return 2; // Second priority - review tasks
            }
            // Already reviewed by this user
            if (item.reviewContributors?.includes(payload.alias)) {
              return 3; // Lowest priority - reviewed items
            }
            return 4; // Other cases
          } else {
            // Fall back to original logic for non-assignment based review
            if (
              item.status === ReviewStatus.REVIEWED ||
              item.reviewContributors?.includes(payload.alias)
            ) {
              return 3;
            }
            return 2;
          }
        };

        const priorityA = getItemPriority(a);
        const priorityB = getItemPriority(b);

        // First sort by priority
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // If same priority, sort by sourceId
        return a.sourceId.localeCompare(b.sourceId);
      });
    },
    resetListViewPage: (state) => {
      state.getListViewLoading = "idle";
      state.getGroundTruthDataLoading = "idle";
      state.getGroundTruthDataStatistics = "idle";
      state.updateGroundTruthDataLoading = "idle";
      state.translationLoading = "idle";
      state.textHighlightLoading = "idle";
      state.highlightField = "";
      state.translation = "";
      state.showTranslation = false;
    },
    resetGroundTruthReviewPage: (state) => {
      state.getGroundTruthDataLoading = "idle";
      state.getGroundTruthDataStatistics = "idle";
      state.updateGroundTruthDataLoading = "idle";
      state.translationLoading = "idle";
      state.textHighlightLoading = "idle";
      state.highlightField = "";
      state.translation = "";
      state.showTranslation = false;
      state.additionalInputsByField = {};
      state.pageLoadTimestamp = generateTimestamp();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGroundTruthDataListView.pending, (state) => {
      state.getListViewLoading = "pending";
      state.groundTruthDataTotalCount = 0;
      state.groundTruthDataTotalReviewedCount = 0;
    });
    builder.addCase(getBatchRecordList.pending, (state) => {
      state.getListViewLoading = "pending";
      state.groundTruthDataTotalCount = 0;
      state.groundTruthDataTotalReviewedCount = 0;
    });
    builder.addCase(getGroundTruthDataListView.fulfilled, (state, { payload }) => {
      processListViewData(state, payload);
    });
    builder.addCase(getBatchRecordList.fulfilled, (state, { payload }) => {
      processListViewData(state, convertBatchRecordsToListView(payload));
    });
    builder.addCase(getGroundTruthDataListView.rejected, (state) => {
      state.getListViewLoading = "rejected";
      state.groundTruthDataTotalCount = 0;
      state.groundTruthDataTotalReviewedCount = 0;
    });
    builder.addCase(getBatchRecordList.rejected, (state) => {
      state.getListViewLoading = "rejected";
      state.groundTruthDataTotalCount = 0;
      state.groundTruthDataTotalReviewedCount = 0;
    });
    builder.addCase(assignReviewJobs.pending, (state) => {
      state.assignReviewJobLoading = "pending";
    });
    builder.addCase(assignReviewJobs.fulfilled, (state, { payload }) => {
      state.assignReviewJobLoading = "fulfilled";
    });
    builder.addCase(assignReviewJobs.rejected, (state) => {
      state.assignReviewJobLoading = "rejected";
    });
    builder.addCase(updateAssignment.pending, (state) => {
      state.updateAssignmentLoading = "pending";
    });
    builder.addCase(updateAssignment.fulfilled, (state, { payload }) => {
      state.updateAssignmentLoading = "fulfilled";
    });
    builder.addCase(updateAssignment.rejected, (state) => {
      state.updateAssignmentLoading = "rejected";
    });
    builder.addCase(updateGroundTruthData.pending, (state) => {
      state.updateGroundTruthDataLoading = "pending";
    });
    builder.addCase(updateInsight.pending, (state) => {
      state.updateGroundTruthDataLoading = "pending";
    });
    builder.addCase(updateInsight.fulfilled, (state) => {
      state.updateGroundTruthDataLoading = "fulfilled";
    });
    builder.addCase(updateInsight.rejected, (state) => {
      state.updateGroundTruthDataLoading = "rejected";
    });
    builder.addCase(updateGroundTruthData.fulfilled, (state, { payload }) => {
      state.updateGroundTruthDataLoading = "fulfilled";
      const index = state.groundTruthDataListView.findIndex(
        (groundTruthDataListView: GroundTruthDataListView) =>
          groundTruthDataListView.sourceId === payload.sourceId,
      );
      if (index !== -1) {
        state.groundTruthDataListView[index].status = payload.status;
      }
    });
    builder.addCase(updateGroundTruthData.rejected, (state) => {
      state.updateGroundTruthDataLoading = "rejected";
    });
    builder.addCase(addReviewEntry.fulfilled, (state, { payload }) => {
      state.updateGroundTruthDataLoading = "fulfilled";
      const index = state.groundTruthDataListView.findIndex(
        (groundTruthDataListView: GroundTruthDataListView) =>
          groundTruthDataListView.sourceId === payload.sourceId,
      );
      if (index !== -1) {
        state.groundTruthDataListView[index].status = payload.status;
      }
    });
    builder.addCase(addReviewEntry.rejected, (state) => {
      state.updateGroundTruthDataLoading = "rejected";
    });

    builder.addCase(translate.pending, (state) => {
      state.translationLoading = "pending";
      state.translation = "";
      state.showTranslation = false;
    });
    builder.addCase(translate.fulfilled, (state, { payload }) => {
      state.translationLoading = "fulfilled";
      state.translation = payload;
      state.showTranslation = true;
    });
    builder.addCase(translate.rejected, (state) => {
      state.translationLoading = "rejected";
      state.showTranslation = false;
    });
    builder.addCase(extractTextHighlightByFieldDefinition.pending, (state) => {
      state.textHighlightLoading = "pending";
    });
    builder.addCase(extractTextHighlightByFieldDefinition.fulfilled, (state, { payload }) => {
      state.textHighlightLoading = "fulfilled";
      state.showHighlight = true;

      // Update llmClarification only for fields that don't have it
      state.selectedGroundTruthData.fields = state.selectedGroundTruthData.fields.map((field) => {
        // Only update if current llmClarification is empty or undefined
        if (!field.llmClarification || field.llmClarification.trim() === "") {
          return {
            ...field,
            llmClarification: payload[field.fieldName]?.join("|") || "",
          };
        }
        // Keep existing field unchanged if it already has llmClarification
        return field;
      });
    });
    builder.addCase(extractTextHighlightByFieldDefinition.rejected, (state) => {
      state.textHighlightLoading = "rejected";
      state.showHighlight = false;
    });
    builder.addCase(getBatchRecord.fulfilled, (state, action) => {
      const batchRecord = action.payload;
      state.selectedGroundTruthData = {
        modelId: batchRecord.modelId,
        sourceId: batchRecord.sourceId,
        sourceText: batchRecord.sourceText,
        fields: batchRecord.extractedResult.map((field: BatchExtractedField) => ({
          fieldName: field.fieldName,
          userAnswer: "",
          llmClarification: field.llmClarification,
          llmAnswer: field.result,
          confirmedAnswer: "",
          reviewResult: ReviewResult.NOT_REVIEWED_YET,
        })),
        timestamp: batchRecord.timestamp,
        status: ReviewStatus.UNREVIEWED,
        reviewer: "",
      };
      state.showUserAnswerColumn = false;
      state.showLLMAnswerColumn = true;
      state.isBlind = false;
      state.getGroundTruthDataLoading = "fulfilled";
    });
    builder.addMatcher(
      isAnyOf(
        getGroundTruthData.rejected,
        getRandomInsight.rejected,
        getInsightBySourceId.rejected,
        getBatchRecord.rejected,
      ),
      (state) => {
        state.getGroundTruthDataLoading = "rejected";
      },
    );
    builder.addMatcher(
      isAnyOf(
        getGroundTruthData.pending,
        getRandomInsight.pending,
        getInsightBySourceId.pending,
        getBatchRecord.pending,
      ),
      (state) => {
        state.getGroundTruthDataLoading = "pending";
      },
    );
    builder.addMatcher(
      isAnyOf(
        getGroundTruthData.fulfilled,
        getRandomInsight.fulfilled,
        getInsightBySourceId.fulfilled,
      ),
      (state, { payload }) => {
        state.getGroundTruthDataLoading = "fulfilled";
        state.selectedGroundTruthData = setLLMAnswerForUnreviewedGroundTruthData(payload);
        if (state.selectedGroundTruthData.reviewContributors) {
          state.selectedGroundTruthData.reviewContributors =
            state.selectedGroundTruthData.reviewContributors.map((alias: string) =>
              alias.toLowerCase(),
            );
        }
        if (state.selectedGroundTruthData.pendingReviewers) {
          state.selectedGroundTruthData.pendingReviewers =
            state.selectedGroundTruthData.pendingReviewers.map((alias: string) =>
              alias.toLowerCase(),
            );
        }
        if (state.selectedGroundTruthData.adminReviewers) {
          state.selectedGroundTruthData.adminReviewers =
            state.selectedGroundTruthData.adminReviewers.map((alias: string) =>
              alias.toLowerCase(),
            );
        }
        state.selectedGroundTruthData.fields = state.selectedGroundTruthData.fields.map((field) => {
          if (typeof field.confirmedAnswer === "string" && field.confirmedAnswer.includes("|")) {
            return {
              ...field,
              confirmedAnswer: field.confirmedAnswer
                .split("|")
                .map((answer) => answer.trim())
                .filter((answer) => answer !== ""), // Remove empty strings
            };
          }
          return field;
        });
        // Set default column visibility based on field content
        // Metadata-based configuration will be handled by the updateColumnVisibilityFromModelMetadata action
        state.showUserAnswerColumn = state.selectedGroundTruthData.fields.some(
          (field) => field.userAnswer.trim() !== "",
        );
        state.showLLMAnswerColumn = state.selectedGroundTruthData.fields.some(
          (field) => field.llmAnswer.trim() !== "",
        );
        state.pageLoadTimestamp = generateTimestamp();
      },
    );
  },
});

export const {
  setFieldConfirmedAnswer,
  setListViewIndex,
  setCurrentPage,
  setIsBlind,
  setItemPerPage,
  resetGroundTruthReviewPage,
  resetListViewPage,
  setShowTranslation,
  setShowHighlight,
  setHighlightField,
  updateConfirmedAnswersFromHistory,
  sortAndFilterGroundTruthDataListView,
  setAssignmentCSV,
  setShouldShowError,
  setFieldKnowledge,
  setFieldAdditionalInput,
  setMoveToNextWhenSave,
  updateColumnVisibilityFromModelMetadata,
  setAliasFilter,
  applyAliasFilter,
} = actions;

// Re-export types and enums from the service for convenience
export { ReviewerAssignmentTypeEnum } from "src/services/modelLifeCycleService";
export type { UpdateAssignmentPayload } from "src/services/modelLifeCycleService";

export default reducer;
